import React, { useState, useEffect, useRef } from 'react';
import { getCurrencySymbol, formatCurrency, formatCompactCurrency } from '../utils/currency';
import axios from 'axios';
import { API, useAuth } from '../App';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import PaginationControls from '../components/PaginationControls';
import usePagination from '../hooks/usePagination';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SupplyCategoryField, TreatmentBadge, useTaxCategories, reclassifyItem } from '../components/tax-catalogue/SupplyCategoryField';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { CurrencyInput } from '../components/ui/currency-input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Briefcase,
  Plus,
  Edit3,
  Trash2,
  Search,
  TrendingUp,
  DollarSign,
  BarChart3,
  Tag,
  MoreVertical,
  Archive,
  Upload,
  Download,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  Users
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ContactsTab from '../components/contacts/ContactsTab';

const ProductsServicesPage = () => {
  const { business } = useAuth();
  const [mainTab, setMainTab] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'contacts' ? 'contacts' : 'products'); // 'products' or 'contacts'
  const [pendingProductId, setPendingProductId] = useState(() => new URLSearchParams(window.location.search).get('product'));
  const [activeTab, setActiveTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // CSV Upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const csvInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'service',
    description: '',
    price: '',
    unit: '',
    taxable: true,
    category: '',
    sku: '',
    create_revenue_gl: false,  // New field for GL creation
    // iter1094-product-tax-dropdown - country-aware tax defaults
    tax_code: '',
    supply_category: '',
    tax_rate: 0,
  });

  // iter1094-product-tax-dropdown - vocabulary is fetched once on
  // mount. Reused by both the create dialog and the edit dialog.
  const [taxOptions, setTaxOptions] = useState([]);
  const taxCategories = useTaxCategories();
  const [reclassifying, setReclassifying] = useState(null);
  const handleReclassify = async (item) => {
    setReclassifying(item.id);
    const data = await reclassifyItem(item.id);
    if (data) setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, ...data } : it)));
    setReclassifying(null);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tax-management/rules`);
        setTaxOptions(
          (data?.rules || []).map((r) => ({
            code: r.code || r.tax_code,
            name: r.name || r.label || r.code,
            rate: r.rate ?? 0,
          }))
        );
      } catch { /* fall back silently - dropdown just won't render */ }
    })();
  }, []);

  const categories = [
    'Consulting',
    'Development',
    'Design',
    'Marketing',
    'Maintenance',
    'Training',
    'Hardware',
    'Software',
    'Subscription',
    'Retail Product',
    'Wholesale Product',
    'Other'
  ];

  const units = [
    { value: 'hour', label: 'Per Hour' },
    { value: 'day', label: 'Per Day' },
    { value: 'project', label: 'Per Project' },
    { value: 'unit', label: 'Per Unit' },
    { value: 'month', label: 'Per Month' },
    { value: 'year', label: 'Per Year' }
  ];

  const formatAmount = (amount) => {
    return formatCurrency(amount, business?.currency || 'NGN');
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/products-services`);
      setItems(response.data);
    } catch (error) {
      console.log('Fetching products/services...', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/products-services/${editingItem.id}`, formData);
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API}/products-services`, formData);
        toast.success('Item created successfully');
      }
      fetchItems();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/products-services/${id}`);
      toast.success('Item deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetForm = () => {
    setShowDialog(false);
    setEditingItem(null);
    setFormData({
      name: '',
      type: 'service',
      description: '',
      price: '',
      unit: '',
      taxable: true,
      category: '',
      sku: '',
      create_revenue_gl: false,
      tax_code: '',
    supply_category: '',
      tax_rate: 0,
    });
  };

  // CSV Upload Functions
  const downloadTemplate = () => {
    const template = `name,type,price,description,category,unit,sku,taxable
"Web Design Package",service,1500,"Complete website design and development","Development","project","",true
"Logo Design",service,500,"Professional logo design with 3 revisions","Design","project","",true
"Office Chair",product,250,"Ergonomic office chair","Retail Product","unit","SKU-001",true
"Monthly Support",service,200,"Monthly technical support","Maintenance","month","",true`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return { data: [], errors: ['CSV file must have a header row and at least one data row'] };
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const requiredHeaders = ['name', 'type', 'price'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      return { data: [], errors: [`Missing required columns: ${missingHeaders.join(', ')}`] };
    }
    
    const data = [];
    const errors = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      // Parse CSV line handling quoted values
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      if (values.length < headers.length) {
        errors.push(`Row ${i + 1}: Not enough columns`);
        continue;
      }
      
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx]?.replace(/"/g, '') || '';
      });
      
      // Validate row
      if (!row.name) {
        errors.push(`Row ${i + 1}: Name is required`);
        continue;
      }
      if (!row.type || !['service', 'product'].includes(row.type.toLowerCase())) {
        errors.push(`Row ${i + 1}: Type must be 'service' or 'product'`);
        continue;
      }
      if (!row.price || isNaN(parseFloat(row.price))) {
        errors.push(`Row ${i + 1}: Valid price is required`);
        continue;
      }
      
      data.push({
        name: row.name,
        type: row.type.toLowerCase(),
        price: parseFloat(row.price),
        description: row.description || '',
        category: row.category || '',
        unit: row.unit || '',
        sku: row.sku || '',
        taxable: row.taxable?.toLowerCase() !== 'false'
      });
    }
    
    return { data, errors };
  };

  const handleCSVSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      const { data, errors } = parseCSV(text);
      setCsvPreview(data);
      setCsvErrors(errors);
      setShowUploadDialog(true);
    };
    reader.readAsText(file);
    
    // Reset input
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const confirmCSVUpload = async () => {
    if (csvPreview.length === 0) {
      toast.error('No valid items to upload');
      return;
    }
    
    setUploadingCSV(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const item of csvPreview) {
      try {
        await axios.post(`${API}/products-services`, item);
        successCount++;
      } catch (err) {
        failCount++;
      }
    }
    
    setUploadingCSV(false);
    setShowUploadDialog(false);
    setCsvPreview([]);
    setCsvErrors([]);
    
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} items${failCount > 0 ? ` (${failCount} failed)` : ''}`);
      fetchItems();
    } else {
      toast.error('Failed to import items');
    }
  };

  // Sep 2026 · global search deep link: /products-services?product=<id> opens that product for editing once loaded
  useEffect(() => {
    if (!pendingProductId || !items.length) return;
    const it = items.find((x) => x.id === pendingProductId);
    if (it) { openEditDialog(it); setPendingProductId(null); }
  }, [pendingProductId, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      type: item.type,
      description: item.description || '',
      price: item.price.toString(),
      unit: item.unit || '',
      taxable: item.taxable !== false,
      category: item.category || '',
      sku: item.sku || '',
      create_revenue_gl: false,
      // iter1094 - hydrate persisted tax defaults on edit
      tax_code: item.tax_code || '',
      tax_rate: item.tax_rate ?? 0,
      supply_category: item.supply_category || '',
    });
    setShowDialog(true);
  };

  const filteredItems = items.filter(item => {
    const matchesTab = activeTab === 'all' || item.type === activeTab;
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const itemsPager = usePagination(filteredItems, `${activeTab}|${searchTerm}`);

  const stats = {
    total: items.length,
    services: items.filter(i => i.type === 'service').length,
    products: items.filter(i => i.type === 'product').length,
    avgPrice: items.length > 0 ? items.reduce((sum, i) => sum + i.price, 0) / items.length : 0
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="products-contacts-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Products & Contacts</h1>
            <p className="text-muted-foreground mt-1">Add what you sell and who you do business with</p>
          </div>
        </div>

        {/* Main Tabs: Products & Services vs Contacts */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="products" className="flex items-center gap-2" data-testid="products-tab">
              <Package className="w-4 h-4" />
              Products & Services
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2" data-testid="contacts-main-tab">
              <Users className="w-4 h-4" />
              Contacts
            </TabsTrigger>
          </TabsList>

          {/* Products & Services Tab Content */}
          <TabsContent value="products">
            <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-4 mb-6">
              <div className="flex items-center gap-2">
                {/* CSV Upload */}
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  ref={csvInputRef}
                  onChange={handleCSVSelect}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="import-btn">
                      <Upload className="w-4 h-4" />
                      Import
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => csvInputRef.current?.click()}>
                      <FileText className="w-4 h-4 mr-2" />
                      Upload CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadTemplate}>
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Dialog open={showDialog} onOpenChange={(open) => { if (!open) resetForm(); else setShowDialog(true); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#283F8B] hover:bg-[#283F8B]/90 gap-2" data-testid="add-item-btn">
                      <Plus className="w-5 h-5" />
                      Add New
                    </Button>
                  </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>{editingItem ? 'Edit Item' : 'Add Product or Service'}</DialogTitle>
                <DialogDescription>
                  Add items you sell to quickly add them to invoices
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 overflow-y-auto flex-1">
                {/* Type Selection */}
                <div className="flex gap-4">
                  <motion.button
                    type="button"
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      formData.type === 'service' 
                        ? 'border-[#283F8B] bg-[#283F8B]/5' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    onClick={() => setFormData({ ...formData, type: 'service' })}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Briefcase className={`w-6 h-6 mx-auto mb-2 ${formData.type === 'service' ? 'text-[#283F8B]' : 'text-muted-foreground'}`} />
                    <span className={`font-medium ${formData.type === 'service' ? 'text-[#283F8B]' : 'text-muted-foreground'}`}>Service</span>
                  </motion.button>
                  <motion.button
                    type="button"
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      formData.type === 'product' 
                        ? 'border-[#108981] bg-[#108981]/5' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    onClick={() => setFormData({ ...formData, type: 'product' })}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Package className={`w-6 h-6 mx-auto mb-2 ${formData.type === 'product' ? 'text-[#108981]' : 'text-muted-foreground'}`} />
                    <span className={`font-medium ${formData.type === 'product' ? 'text-[#108981]' : 'text-muted-foreground'}`}>Product</span>
                  </motion.button>
                </div>

                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    placeholder={formData.type === 'service' ? 'e.g. Web Development' : 'e.g. Laptop Stand'}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12"
                    data-testid="input-item-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Price *</Label>
                    <CurrencyInput
                      value={formData.price}
                      onChange={(value) => setFormData({ ...formData, price: value })}
                      currencySymbol={getCurrencySymbol()}
                      placeholder="0.00"
                      className="h-12"
                      data-testid="input-item-price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit</Label>
                    <Select 
                      value={formData.unit} 
                      onValueChange={(v) => setFormData({ ...formData, unit: v })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map((u) => (
                          <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.type === 'product' && (
                  <div className="space-y-2">
                    <Label>SKU (Stock Keeping Unit)</Label>
                    <Input
                      placeholder="e.g. PROD-001"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="h-12"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Textarea
                    placeholder="Describe this item..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <SupplyCategoryField value={formData.supply_category} onChange={(v) => setFormData({ ...formData, supply_category: v })} categories={taxCategories} taxLabel={taxCategories?.tax_label || 'VAT'} />

                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                  <div>
                    <p className="font-medium text-foreground">Taxable</p>
                    <p className="text-sm text-muted-foreground">Apply tax when invoicing</p>
                  </div>
                  <Switch
                    checked={formData.taxable}
                    onCheckedChange={(checked) => setFormData({ ...formData, taxable: checked })}
                    data-testid="product-create-taxable"
                  />
                </div>

                {/* iter1094-product-tax-dropdown - country-aware tax
                    code dropdown. Populated from /api/tax-management/rules
                    (US Sales-Tax categories or country VAT rules). Only
                    shown when the product is taxable AND we have options. */}
                {formData.taxable && taxOptions.length > 0 && (
                  <div className="space-y-2">
                    <Label>Default Tax Code</Label>
                    <Select
                      value={formData.tax_code || 'NONE'}
                      onValueChange={(value) => {
                        if (value === 'NONE') {
                          setFormData({ ...formData, tax_code: '', tax_rate: 0 });
                          return;
                        }
                        const opt = taxOptions.find((o) => o.code === value);
                        setFormData({
                          ...formData,
                          tax_code: value,
                          tax_rate: opt?.rate ?? 0,
                        });
                      }}
                    >
                      <SelectTrigger className="h-11" data-testid="product-create-tax-code">
                        <SelectValue placeholder="Select default tax rate">
                          {formData.tax_code
                            ? `${taxOptions.find((o) => o.code === formData.tax_code)?.name || formData.tax_code} · ${formData.tax_rate}%`
                            : 'No default tax'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="NONE">No default tax</SelectItem>
                        {taxOptions.map((opt) => (
                          <SelectItem key={opt.code} value={opt.code}>
                            <span className="flex items-center justify-between gap-3 w-full">
                              <span className="truncate">{opt.name}</span>
                              <span className="text-muted-foreground text-xs shrink-0">{opt.rate}%</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      This rate will auto-select when adding this item to an invoice.
                    </p>
                  </div>
                )}

                {/* Revenue GL Account Option */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#283F8B]/5 to-[#108981]/5 rounded-xl border border-[#283F8B]/20">
                  <div>
                    <p className="font-medium text-foreground flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-[#283F8B]" />
                      Create Revenue GL Account
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Track revenue for this {formData.type} separately in the General Ledger
                    </p>
                  </div>
                  <Switch
                    checked={formData.create_revenue_gl}
                    onCheckedChange={(checked) => setFormData({ ...formData, create_revenue_gl: checked })}
                    data-testid="create-revenue-gl-switch"
                  />
                </div>

                {formData.create_revenue_gl && (
                  <div className="p-3 bg-[#108981]/10 rounded-lg text-sm text-[#108981]">
                    <p className="font-medium">A new GL account will be created:</p>
                    <p className="mt-1">Revenue - {formData.name || `[${formData.type}]`}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      This allows you to see exactly how much revenue this {formData.type} generates in your financial reports.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" className="flex-1" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-[#108981] hover:bg-[#108981]/90" 
                    onClick={handleSubmit}
                    data-testid="save-item-btn"
                  >
                    {editingItem ? 'Update' : 'Save'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#283F8B] mb-2">
                <Tag className="w-5 h-5" />
                <span className="text-sm font-medium">Total Items</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('service')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#5FC6C5] mb-2">
                <Briefcase className="w-5 h-5" />
                <span className="text-sm font-medium">Services</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.services}</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('product')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#108981] mb-2">
                <Package className="w-5 h-5" />
                <span className="text-sm font-medium">Products</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.products}</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-500 mb-2">
                <DollarSign className="w-5 h-5" />
                <span className="text-sm font-medium">Avg Price</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{formatAmount(stats.avgPrice)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search & Tabs */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search products & services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'service', 'product'].map((tab) => (
              <Button
                key={tab}
                variant={activeTab === tab ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab)}
                className={activeTab === tab ? 'bg-[#283F8B]' : ''}
              >
                {tab === 'all' ? 'All' : tab === 'service' ? 'Services' : 'Products'}
              </Button>
            ))}
          </div>
        </div>

        {/* Items List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredItems.length > 0 ? (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {itemsPager.pageItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="hover:shadow-lg transition-all group" data-testid={`item-${item.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        {item.image_thumb_url || item.image_url ? (
                          <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-100">
                            <img src={item.image_thumb_url || item.image_url} alt={item.name} className="w-full h-full object-cover" data-testid={`product-photo-${item.id}`} />
                          </div>
                        ) : (
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          item.type === 'service' 
                            ? 'bg-[#283F8B]/10' 
                            : 'bg-[#108981]/10'
                        }`}>
                          {item.type === 'service' ? (
                            <Briefcase className="w-6 h-6 text-[#283F8B]" />
                          ) : (
                            <Package className="w-6 h-6 text-[#108981]" />
                          )}
                        </div>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <MoreVertical className="w-5 h-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(item)}>
                              <Edit3 className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      <h3 className="font-semibold text-foreground mb-1">{item.name}</h3>
                      <div className="mb-2"><TreatmentBadge item={item} taxLabel={taxCategories?.tax_label || 'VAT'} onReclassify={() => handleReclassify(item)} busy={reclassifying === item.id} /></div>
                      {item.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{item.description}</p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className={
                            item.type === 'service' ? 'bg-[#283F8B]/10 text-[#283F8B]' : 'bg-[#108981]/10 text-[#108981]'
                          }>
                            {item.type}
                          </Badge>
                          {item.category && (
                            <Badge variant="outline" className="text-xs">
                              {item.category}
                            </Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-foreground">{formatAmount(item.price)}</p>
                          {item.unit && (
                            <p className="text-xs text-muted-foreground">
                              {units.find(u => u.value === item.unit)?.label || item.unit}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <PaginationControls {...itemsPager.controlProps} testId="products-pagination" />
          </>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-medium text-foreground mb-2">
                {searchTerm ? 'No items found' : 'No products or services yet'}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {searchTerm 
                  ? 'Try a different search term'
                  : 'Add your products and services to use them when creating invoices and bills'
                }
              </p>
              {!searchTerm && (
                <Button 
                  className="bg-[#283F8B] hover:bg-[#283F8B]/90"
                  onClick={() => setShowDialog(true)}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Your First Item
                </Button>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* CSV Upload Preview Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#283F8B]" />
                Import Products & Services
              </DialogTitle>
              <DialogDescription>
                Review the items before importing
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Errors */}
              {csvErrors.length > 0 && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm font-medium text-destructive mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    {csvErrors.length} error(s) found
                  </p>
                  <ul className="text-xs text-destructive/80 space-y-1 max-h-24 overflow-y-auto">
                    {csvErrors.map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Preview */}
              {csvPreview.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    {csvPreview.length} item(s) ready to import
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 font-medium">Name</th>
                          <th className="text-left p-2 font-medium">Type</th>
                          <th className="text-left p-2 font-medium">Category</th>
                          <th className="text-right p-2 font-medium">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.slice(0, 10).map((item, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/30'}>
                            <td className="p-2">{item.name}</td>
                            <td className="p-2">
                              <Badge variant="secondary" className="text-xs">
                                {item.type}
                              </Badge>
                            </td>
                            <td className="p-2 text-muted-foreground">{item.category || '-'}</td>
                            <td className="p-2 text-right font-medium">{formatAmount(item.price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvPreview.length > 10 && (
                      <p className="text-center text-sm text-muted-foreground py-2 bg-muted/50">
                        ... and {csvPreview.length - 10} more items
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {csvPreview.length === 0 && csvErrors.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No items found in the CSV file
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={confirmCSVUpload}
                disabled={csvPreview.length === 0 || uploadingCSV}
                className="bg-[#283F8B] hover:bg-[#283F8B]/90"
                data-testid="confirm-csv-upload-btn"
              >
                {uploadingCSV ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Import {csvPreview.length} Items
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </TabsContent>

          {/* Contacts Tab Content */}
          <TabsContent value="contacts">
            <ContactsTab openContactId={new URLSearchParams(window.location.search).get('contact')} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ProductsServicesPage;
